/**
 * Scaffold Generation Context
 * Global background scaffold generation — Google Drive-style
 * Allows users to navigate freely while scaffolds generate in the background
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { startScaffoldGenerationJob, getScaffoldGenerationJob } from '../api/courses';

export interface ScaffoldGenTask {
  id: string;
  courseId: string;
  topics: string[];
  currentIndex: number;
  successCount: number;
  failCount: number;
  totalTopics: number;
  currentTopic: string;
  status: 'generating' | 'done' | 'error';
  startTime: number;
}

interface ScaffoldGenerationContextValue {
  tasks: ScaffoldGenTask[];
  startGeneration: (
    courseId: string,
    topics: string[],
    lectureIds: string[],
    slideIds: string[],
    preset: string,
    onComplete?: () => void,
    fileIds?: string[],
  ) => void;
  dismissTask: (id: string) => void;
  dismissCompleted: () => void;
  hasActiveGenerations: boolean;
}

const ScaffoldGenerationContext = createContext<ScaffoldGenerationContextValue | undefined>(undefined);

export const ScaffoldGenerationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<ScaffoldGenTask[]>([]);
  const autoDismissTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const hasActiveGenerations = tasks.some(t => t.status === 'generating');

  // Warn before closing tab during active generation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveGenerations) {
        e.preventDefault();
        e.returnValue = 'Scaffolds are still generating. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveGenerations]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      autoDismissTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const dismissTask = useCallback((id: string) => {
    const timer = autoDismissTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      autoDismissTimers.current.delete(id);
    }
    setTasks(prev => prev.filter(t => t.id !== id));
  }, []);

  const dismissCompleted = useCallback(() => {
    setTasks(prev => prev.filter(t => t.status === 'generating'));
    autoDismissTimers.current.forEach((timer, id) => {
      clearTimeout(timer);
      autoDismissTimers.current.delete(id);
    });
  }, []);

  const scheduleAutoDismiss = useCallback((id: string) => {
    const timer = setTimeout(() => {
      autoDismissTimers.current.delete(id);
      setTasks(prev => prev.filter(t => t.id !== id));
    }, 10000);
    autoDismissTimers.current.set(id, timer);
  }, []);

  const startGeneration = useCallback((
    courseId: string,
    topics: string[],
    lectureIds: string[],
    slideIds: string[],
    preset: string,
    onComplete?: () => void,
    fileIds?: string[],
  ) => {
    const id = `scaffgen_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const task: ScaffoldGenTask = {
      id,
      courseId,
      topics,
      currentIndex: 0,
      successCount: 0,
      failCount: 0,
      totalTopics: topics.length,
      currentTopic: topics[0],
      status: 'generating',
      startTime: Date.now(),
    };

    setTasks(prev => [...prev, task]);

    // Run generation loop asynchronously
    (async () => {
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < topics.length; i++) {
        const topic = topics[i];

        // Update progress
        setTasks(prev => prev.map(t => t.id === id ? {
          ...t,
          currentIndex: i,
          currentTopic: topic,
          successCount,
          failCount,
        } : t));

        try {
          // Submit as a background job and poll: generation can take minutes,
          // and holding an HTTP connection that long gets cut by the proxy
          const job = await startScaffoldGenerationJob(courseId, {
            title: topic,
            lecture_ids: lectureIds,
            slide_ids: slideIds,
            file_ids: fileIds ?? [],
            topic,
            preset,
          });
          let finished = false;
          for (let poll = 0; poll < 150; poll++) {
            await new Promise(resolve => setTimeout(resolve, 4000));
            try {
              const status = await getScaffoldGenerationJob(courseId, job.job_id);
              if (status.status === 'done') {
                successCount++;
                finished = true;
                break;
              }
              if (status.status === 'failed') {
                console.error(`Scaffold generation failed for topic "${topic}":`, status.error);
                failCount++;
                finished = true;
                break;
              }
            } catch {
              // transient poll failure: keep waiting
            }
          }
          if (!finished) {
            console.error(`Scaffold generation timed out for topic "${topic}"`);
            failCount++;
          }
        } catch (err: any) {
          console.error(`Failed to start scaffold generation for topic "${topic}":`, err);
          failCount++;
        }
      }

      // Mark as done
      setTasks(prev => prev.map(t => t.id === id ? {
        ...t,
        currentIndex: topics.length,
        successCount,
        failCount,
        status: failCount === topics.length ? 'error' : 'done',
        currentTopic: '',
      } : t));

      scheduleAutoDismiss(id);
      onComplete?.();
    })();
  }, [scheduleAutoDismiss]);

  return (
    <ScaffoldGenerationContext.Provider value={{
      tasks,
      startGeneration,
      dismissTask,
      dismissCompleted,
      hasActiveGenerations,
    }}>
      {children}
    </ScaffoldGenerationContext.Provider>
  );
};

export const useScaffoldGeneration = (): ScaffoldGenerationContextValue => {
  const context = useContext(ScaffoldGenerationContext);
  if (!context) {
    throw new Error('useScaffoldGeneration must be used within a ScaffoldGenerationProvider');
  }
  return context;
};
